<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';

export interface TabItem {
  id: string;
  label?: string;
}

const props = defineProps<{
  modelValue: string;
  tabs: TabItem[];
}>();

const emit = defineEmits(['update:modelValue']);

const tabsEl = ref<HTMLElement>();

// A selection made from outside (deep link) can land on a tab scrolled out of view.
async function scrollActiveTabIntoView() {
  await nextTick();
  tabsEl.value
    ?.querySelector('.tabs-bar__tab--active')
    ?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
}

onMounted(scrollActiveTabIntoView);
watch(() => props.modelValue, scrollActiveTabIntoView);
</script>

<template>
  <div ref="tabsEl" class="tabs-bar" role="tablist">
    <button
      v-for="tab in tabs"
      :key="tab.id"
      class="tabs-bar__tab"
      :class="{ 'tabs-bar__tab--active': tab.id === modelValue }"
      type="button"
      role="tab"
      :aria-selected="tab.id === modelValue"
      @click="emit('update:modelValue', tab.id)"
    >
      <span class="tabs-bar__content">
        <slot name="tab" :tab="tab" :active="tab.id === modelValue">
          <span class="tabs-bar__label">{{ tab.label }}</span>
        </slot>
      </span>
    </button>
  </div>
</template>

<style lang="scss">
// More tabs than a phone width holds scroll horizontally rather than wrap
.tabs-bar {
  border-bottom: 1px solid $color-border-light;
  display: flex;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &__tab {
    background: none;
    border: none;
    cursor: pointer;
    // Tab stretched by its parent (flex: 1) : the content stays centered and
    // sized to the label, so the active underline hugs the text.
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    padding: 0 $spacing-4;

    &--active .tabs-bar__content::after {
      background-color: $color-neutral-highest;
      border-radius: 8px 8px 0 0;
      bottom: 0;
      content: '';
      left: 0;
      position: absolute;
      right: 0;
      height: 3px;
    }
  }

  &__content {
    align-items: center;
    display: flex;
    gap: 10px;
    justify-content: center;
    padding: 14px 0;
    position: relative;
  }

  &__label {
    color: $color-neutral-highest;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    white-space: nowrap;
  }
}
</style>
